import { useState, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CalendarDetailHeader } from '@/components/Calendars/CalendarDetailHeader'
import { EventsList } from '@/components/Calendars/EventsList'
import { CalendarGridView } from '@/components/Calendars/CalendarGridView'
import { CalendarTimelineView } from '@/components/Calendars/CalendarTimelineView'
import { MilestoneEditorPanel } from '@/components/Calendars/MilestoneEditorPanel'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { useCalendarDetail, useDeleteCalendar } from '@/hooks/useCalendars'
import { cn } from '@/utils/cn'

type TabValue = 'events' | 'grid' | 'timeline'

const TABS: { value: TabValue; label: string }[] = [
  { value: 'events', label: 'Events' },
  { value: 'grid', label: 'Grid View' },
  { value: 'timeline', label: 'Timeline' },
]

export default function AdminCalendarDetail() {
  const { calendarId } = useParams<{ calendarId: string }>()
  const navigate = useNavigate()

  const { data: calendar, isLoading, isError } = useCalendarDetail(calendarId)
  const showInitialLoading = isLoading && !calendar
  const deleteCalendar = useDeleteCalendar()

  const [activeTab, setActiveTab] = useState<TabValue>('events')
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const selectedEvent = useMemo(() => {
    if (!selectedEventId || !calendar) return null
    return calendar.events.find(e => e.id === selectedEventId) ?? null
  }, [selectedEventId, calendar])

  const handleDelete = useCallback(() => {
    setDeleteDialogOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(() => {
    if (!calendarId) return
    deleteCalendar.mutate(calendarId, {
      onSuccess: () => {
        setDeleteDialogOpen(false)
        navigate('/admin/calendars')
      },
    })
  }, [calendarId, deleteCalendar, navigate])

  const handleEventClick = useCallback((eventId: string) => {
    setSelectedEventId(eventId)
  }, [])

  const handleClosePanel = useCallback(() => {
    setSelectedEventId(null)
  }, [])

  if (showInitialLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (isError || !calendar) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24">
        <p className="text-sm text-muted-foreground">
          {isError ? 'Failed to load calendar.' : 'Calendar not found.'}
        </p>
        <Button
          variant="outline"
          onClick={() => navigate('/admin/calendars')}
          className="cursor-pointer"
        >
          Back to Calendars
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <CalendarDetailHeader
        calendar={calendar}
        calendarId={calendarId!}
        onDelete={handleDelete}
      />

      {/* Tab Bar */}
      <div className="inline-flex w-fit rounded-lg border border-accent-border bg-accent p-1">
        {TABS.map(tab => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={cn(
              'px-4 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer',
              'text-[#13254f]',
              activeTab === tab.value
                ? 'bg-white shadow-sm'
                : 'hover:bg-white/50'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'events' && (
        <EventsList
          events={calendar.events}
          calendarId={calendarId!}
          onEventClick={handleEventClick}
        />
      )}
      {activeTab === 'grid' && <CalendarGridView calendarId={calendarId!} />}
      {activeTab === 'timeline' && <CalendarTimelineView calendar={calendar} />}

      {/* Milestone Editor Panel */}
      {activeTab === 'events' && selectedEvent && (
        <MilestoneEditorPanel
          calendarId={calendarId!}
          event={selectedEvent}
          allEvents={calendar.events}
          onClose={handleClosePanel}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={open => {
          if (!open) setDeleteDialogOpen(false)
        }}
        title="Delete Calendar"
        description={`Are you sure you want to delete "${calendar.name}"? This will remove all events and milestones. This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={handleDeleteConfirm}
        isLoading={deleteCalendar.isPending}
      />
    </div>
  )
}
